<template>
  <b-card class="!border-[1px] !border-[#E2E2E2] !border-solid !shadow-none !-mt-[10px]">
    <div class="flex items-center justify-start gap-[8px] pb-[16px]">
      <button
        class="rounded-lg button-primary w-[40px] h-[40px] inline-block"
        @click="$router.go(-1)"
      >
        <span
          class="k-arrow-left-2 h-100 font-bold text-10 pt-[3px] align-middle mx-[5px]"
        />
      </button>
      <h3 class="font-semibold text-[#333] text-[20px] mb-0">
        Detail
      </h3>
    </div>
    <tab-component
      v-if="showTabs"
      ref="tabComponent"
      :tabs="tabList"
      :default-tab="defaultTab"
      :enabled-tab="enabledTab"
      :disabled="isCancelOrEnded"
      without-count
      :variant="{
        active: 'primary',
        inactive: 'light',
      }"
      @tab-change="handleTabChange"
    />
    <general-card
      :tab="tab"
      :phase="effectivePhase"
      :campaign="campaign"
      :loading="loading"
      :error="error"
      :mutating="mutatingCampaignEnd"
      :mutating-cancel="mutatingCampaignCancel"
      :status="status"
      :disabled="isCancelOrEnded"
      @retry="fetchDetail"
      @campaign-ended="handleCampaignEnded"
      @campaign-cancelled="handleCampaignCancelled"
    />

    <div class="flex justify-between flex-wrap gap-[8px]">
      <filter-component
        ref="filterComponent"
        :phase="effectivePhase"
        @change="handleFilterChange"
      />
      <div
        class="flex items-center justify-center gap-[12px] flex-wrap"
      >
        <b-button
          v-b-popover.hover="{ content: 'Export data creator', customClass: 'scala-popover' }"
          variant="outline-primary"
          class="!flex items-center justify-center gap-[14px] !rounded-[8px]"
          :disabled="isExportDisabled"
          @click="exportData"
        ><span
          v-if="!loadingExportData"
          class="k-document-download"
        /><b-spinner
          v-else
          small
          variant="primary"
          class="mx-auto"
        />Export</b-button>
        <b-button
          v-if="showFinalizeButton"
          variant="primary"
          class="!rounded-[8px]"
          :disabled="isCancelOrEnded || !canFinalized"
          @click="handleFinalize"
        >Finalisasi</b-button>
      </div>
    </div>
    <table-component
      ref="tableComponent"
      :participant="participants"
      :loading="loading"
      :error="error"
      :phase="effectivePhase"
      :disabled="isTableDisabled"
      :content-amount="campaign ? campaign.content_amount : 0"
      @refresh-data="fetchDetail"
    />
    <modal-component
      ref="modalComponent"
    />
  </b-card>
</template>

<script>
import { downloadFileFromBlob } from '@/libs/downloadFile'
import { YEAR_MONTH_DAY } from '@/libs/filterDate'
import TabComponent from '../../creator-management/Tab.vue'
import GeneralCard from './GeneralCard.vue'
import FilterComponent from './Filter.vue'
import TableComponent from './Table.vue'
import ModalComponent from './Modal.vue'

export default {
  components: {
    TabComponent,
    FilterComponent,
    TableComponent,
    GeneralCard,
    ModalComponent,
  },
  data() {
    const {
      tab, keyword, status,
    } = this.$route.query
    return {
      tab: tab || 'pre campaign',
      filter: {
        keyword,
        status,
      },
      selectedCreatorsCount: 0,
      id: this.$route.params.id,
      campaign: undefined,
      loading: true,
      error: undefined,
      updatedParticipants: undefined,
      status: undefined,
      mutatingCampaignEnd: false,
      mutatingCampaignCancel: false,
      loadingExportData: false,
    }
  },
  computed: {
    normalizedStatus() {
      const currentStatus = this.campaign?.status || this.status || this.$route.query.tab || this.$route.query.status || this.tab || ''
      return String(currentStatus).trim().replace(/\s/g, '_').toLowerCase()
    },
    defaultTab() {
      if (this.effectivePhase === 'ongoing') return 'ongoing'
      return 'pre campaign'
    },
    enabledTab() {
      if (this.isCancelOrEnded) return undefined
      if (this.status === 'pre_campaign') return 'pre campaign'
      if (this.status === 'ongoing') return 'ongoing'
      return undefined
    },
    tabList() {
      return ['Pre Campaign', 'Ongoing']
    },
    isEnded() {
      return this.normalizedStatus === 'ended'
    },
    isCancelled() {
      return ['cancel', 'cancelled', 'canceled'].includes(this.normalizedStatus)
    },
    isDraft() {
      return this.normalizedStatus === 'draft'
    },
    isCancelOrEnded() {
      return this.isEnded || this.isCancelled
    },
    showTabs() {
      return !this.isDraft
    },
    effectivePhase() {
      if (this.isEnded) return 'ongoing'
      if (this.isCancelled || this.isDraft) return 'pre_campaign'
      const currentTab = (this.tab || this.$route.query.tab || 'pre campaign').toLowerCase()
      return currentTab.includes('ongoing') ? 'ongoing' : 'pre_campaign'
    },
    isTableDisabled() {
      return this.isCancelOrEnded || (this.status === 'ongoing' && this.effectivePhase !== 'ongoing')
    },
    isExportDisabled() {
      return this.isCancelOrEnded || this.loading || this.loadingExportData
    },
    showFinalizeButton() {
      return this.isCancelled || (this.effectivePhase === 'pre_campaign' && this.status === 'pre_campaign')
    },
    participants() {
      const items = this.updatedParticipants || this.campaign?.participants?.items || []
      if (!items || !items.length) return []

      return items.filter(item => {
        let isStatusMatch = true
        if (this.filter.status) {
          isStatusMatch = item.status === this.filter.status
        }
        if (this.filter.status === 'sample_received') {
          isStatusMatch = item.sample === 'diterima'
        }
        if (this.filter.status === 'reimbursement') {
          isStatusMatch = item.reimbursement === 'ya'
        }
        if (this.filter.status === 'uploaded') {
          isStatusMatch = item.uploaded > 0
        }

        let isKeywordMatch = true
        if (this.filter.keyword) {
          const keyword = this.filter.keyword.toLowerCase()
          isKeywordMatch = [item.name, item.tiktok_username, item.phone]
            .some(value => String(value || '').toLowerCase().includes(keyword))
        }

        return isStatusMatch && isKeywordMatch
      })
    },
    canFinalized() {
      return this.campaign?.participants?.items?.length > 0 && this.status !== 'ongoing'
    },
  },
  mounted() {
    this.fetchDetail()
  },
  methods: {
    handleTabChange(tab) {
      if (this.isCancelOrEnded) return
      const prevTab = this.tab
      this.tab = tab
      if (prevTab !== tab) {
        this.fetchDetail()
      }
    },
    handleFilterChange(filter) {
      this.filter = filter
    },
    /**
     * Fetches the campaign detail data.
     * Tries fetching with the currently selected tab's status first.
     * If it fails (e.g., 404 because status changed), it falls back to fetching
     * with the alternative status (ongoing/pre_campaign).
     */
    async fetchDetail() {
      this.loading = true
      const candidateStatuses = []
      const urlTab = (this.$route.query.tab || '').replace(/\s/g, '_').toLowerCase()
      const currentTab = (this.tab || 'pre_campaign').replace(/\s/g, '_').toLowerCase()

      if (urlTab) candidateStatuses.push(urlTab)
      candidateStatuses.push(currentTab)

      if (this.isEnded || urlTab === 'ended' || currentTab === 'ended') {
        candidateStatuses.push('ended', 'ongoing', 'pre_campaign')
      } else if (this.isCancelled || ['cancel', 'cancelled', 'canceled'].some(s => [urlTab, currentTab].includes(s))) {
        candidateStatuses.push('cancel', 'pre_campaign', 'ongoing')
      } else if (this.isDraft || urlTab === 'draft' || currentTab === 'draft') {
        candidateStatuses.push('draft', 'pre_campaign')
      } else if (currentTab === 'ongoing') {
        candidateStatuses.push('ongoing', 'pre_campaign', 'ended')
      } else {
        candidateStatuses.push('pre_campaign', 'ongoing', 'ended', 'cancel')
      }

      const uniqueStatuses = [...new Set(candidateStatuses.filter(Boolean))]

      const fetchWithStatus = reqStatus => this.$http_new.get(`/komclass/api/v1/komlabs/campaigns/${this.id}`, {
        params: {
          status: reqStatus,
        },
      })

      const tryFetch = (index = 0) => {
        if (index >= uniqueStatuses.length) {
          this.error = 'Gagal memuat data'
          this.status = undefined
          this.$toast_error({ message: 'Gagal memuat data' })
          this.loading = false
          return Promise.resolve()
        }

        const reqStatus = uniqueStatuses[index]
        return fetchWithStatus(reqStatus)
          .then(({ data }) => {
            this.campaign = data.data
            this.status = data.data?.status || reqStatus
            if (this.isEnded) {
              this.tab = 'ongoing'
              if (this.$refs.tabComponent) {
                this.$refs.tabComponent.setActiveTab('ongoing')
              }
            } else if (this.isCancelled) {
              this.tab = 'pre campaign'
              if (this.$refs.tabComponent) {
                this.$refs.tabComponent.setActiveTab('pre campaign')
              }
            } else if (this.status === 'ongoing' && this.tab !== 'ongoing') {
              this.tab = 'ongoing'
              if (this.$refs.tabComponent) {
                this.$refs.tabComponent.handleTabChange('ongoing')
              }
            }
            this.error = undefined
            this.loading = false
          })
          .catch(() => tryFetch(index + 1))
      }

      await tryFetch(0)
    },
    handleFinalize() {
      if (this.isCancelOrEnded) return
      this.$refs.modalComponent.showModal({
        callback: () => this.finalizeCampaign(),
        onCancel: () => { },
        title: 'Finalisasi Creator Campaign?',
        description: 'Hanya creator dengan status Diterima yang akan masuk ke ongoing campaign. Apakah Anda yakin ingin melanjutkan?',
        cancelText: 'Batalkan',
        acceptText: 'Ya, Finalisasi',
      })
    },
    finalizeCampaign() {
      const { id: campaignId } = this.$route.params
      return new Promise((resolve, reject) => this.$http_new.put(`/komclass/api/v1/komlabs/campaigns/${campaignId}/status`, {
        status: 'ongoing',
      })
        .then(response => {
          this.status = 'ongoing'
          this.tab = 'ongoing'
          this.$toast_success({ message: 'Campaign berhasil difinalisasi' })
          if (this.$refs.tabComponent) {
            this.$refs.tabComponent.handleTabChange('ongoing')
          }
          resolve(response)
        })
        .catch(error => {
          this.$toast_error({ message: 'Gagal Finalisasi Campaign' })
          reject(error)
        }))
    },
    async handleCampaignEnded() {
      if (this.isCancelOrEnded) return
      this.mutatingCampaignEnd = true
      const { id: campaignId } = this.$route.params
      try {
        await this.$http_new.put(`/komclass/api/v1/komlabs/campaigns/${campaignId}/status`, {
          status: 'ended',
        })
        this.status = 'ended'
        if (this.$refs.tabComponent) {
          this.$refs.tabComponent.handleTabChange('ended')
        }
        this.$toast_success({ message: 'Campaign berhasil diakhiri' })
      } catch (error) {
        this.$toast_error({ message: 'Gagal mengakhiri campaign' })
      } finally {
        this.mutatingCampaignEnd = false
      }
    },
    handleCampaignCancelled() {
      if (this.isCancelOrEnded) return
      this.$refs.modalComponent.showModal({
        callback: () => this.cancelCampaign(),
        onCancel: () => {},
        title: 'Yakin Ingin Batalkan Campaign?',
        description: 'Setelah campaign dibatalkan, creator tidak dapat mendaftar dan campaign akan berstatus Dibatalkan.',
        cancelText: 'Kembali',
        acceptText: 'Ya, Batalkan',
      })
    },
    async cancelCampaign() {
      if (this.isCancelOrEnded) return
      this.mutatingCampaignCancel = true
      const { id: campaignId } = this.$route.params
      try {
        await this.$http_new.delete(`/komclass/api/v1/komlabs/campaigns/${campaignId}/cancel`)
        this.status = 'cancelled'
        this.$toast_success({ message: 'Campaign berhasil dibatalkan' })
        this.$router.go(-1)
      } catch (error) {
        this.$toast_error({ message: 'Gagal membatalkan campaign' })
      } finally {
        this.mutatingCampaignCancel = false
      }
    },
    /**
     * Mengekspor data partisipan campaign ke dalam file Excel.
     */
    exportData() {
      if (this.isExportDisabled) return
      this.loadingExportData = true
      this.$http_new.get(`/komclass/api/v1/komlabs/campaigns/${this.id}/export-participants`, {
        responseType: 'blob',
      }).then(res => {
        if (!res) {
          throw new Error('no data')
        }
        const blob = new Blob([res.data], { type: res.headers['content-type'] })
        downloadFileFromBlob(blob, `detail_creator_campaign_${this.campaign?.name || ''}_${YEAR_MONTH_DAY(new Date())}.xlsx`)
        this.$toast_success({ message: 'Berhasil mengunduh file.' })
      }).catch(() => {
        this.$toast_error({ message: 'Gagal mengunduh file, silahkan coba lagi.' })
      }).finally(() => {
        this.loadingExportData = false
      })
    },
  },
}
</script>
